import React, { useState, useEffect } from 'react';
import {
  Input,
  Table,
  Row,
  Col,
  DatePicker,
  message,
  Button,
  Spin,
  Select,
  Checkbox,
  Modal,
  InputNumber,
} from 'antd';
import { ref, get, update } from 'firebase/database';
import { useFirebase } from '../FirebaseContext';
import { MagnifyingGlass, FileText, CurrencyDollar } from '@phosphor-icons/react';
import moment from 'moment';
import 'moment/locale/pl';
import './Przelewy.css';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  addDoc,
  deleteDoc,
} from 'firebase/firestore';

const { Option } = Select;
const { RangePicker } = DatePicker;

const Przelewy = () => {
  // Получаем актуальные сервисы из контекста
  const { currentFirebase } = useFirebase();
  const db = currentFirebase.database;
  const fs = currentFirebase.firestore;
  // Используйте:
  const {
    config: { firmBankAccount: firmBankAccountConfig },
  } = currentFirebase;

  const [driversData, setDriversData] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [isGenerateModalVisible, setIsGenerateModalVisible] = useState(false);
  const [isGenerateZUSModalVisible, setIsGenerateZUSModalVisible] = useState(false);
  const [isBalancePopupVisible, setIsBalancePopupVisible] = useState(false);
  const [isBankAccountModalVisible, setIsBankAccountModalVisible] = useState(false);
  const [popupDriverId, setPopupDriverId] = useState('');
  const [balanceInput, setBalanceInput] = useState('');
  const [bankAccountInput, setBankAccountInput] = useState('');
  const [balanceAction, setBalanceAction] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filteredDriversData, setFilteredDriversData] = useState([]);
  const [isProjectChanging, setIsProjectChanging] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);

  // --- Duplicate modal state ---
  const [isDuplicateModalVisible, setIsDuplicateModalVisible] = useState(false);
  const [duplicateDriverId, setDuplicateDriverId] = useState('');
  const [duplicateAccounts, setDuplicateAccounts] = useState([]);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [deletingAccountId, setDeletingAccountId] = useState(null);

  // --- ZUS amount state ---
  const [useAutomaticAmount, setUseAutomaticAmount] = useState(true);
  const [manualZUSAmount, setManualZUSAmount] = useState('');

  // Функция обновления таблицы. При каждом изменении базы устанавливаем оверлей, если это не первый рендер
  const updateTable = async () => {
    const startTime = Date.now();
    if (!firstLoad) {
      setIsProjectChanging(true);
    }

    const driverRef = ref(db, 'drivers');
    const driverSnapshot = await get(driverRef);

    const driverDataPromises = [];

    driverSnapshot.forEach((driverSnap) => {
      const driverData = driverSnap.val();
      const driverID = driverSnap.key;
      const driverBalance = parseFloat(driverData['balance']).toFixed(2);

      const driverRecord = {
        key: driverID,
        driverID,
        balance: driverBalance,
        uzDriverId: 'Brak konta użytkownika',
        bankAccount: 'brak',
        accountStatus: 'Brak',
      };

      const driverDataPromise = new Promise(async (resolve) => {
        const q = query(collection(fs, 'users'), where('driverId', '==', driverID));
        const querySnapshot = await getDocs(q);
        if (querySnapshot.empty) {
          resolve(driverRecord);
        } else if (querySnapshot.size === 1) {
          const userDoc = querySnapshot.docs[0];
          const userID = userDoc.id;
          driverRecord.uzDriverId = userID;

          const danePodatkoweRef = doc(fs, `users/${userID}/dokumenty`, 'dane_podatkowe');
          const danePodatkoweDoc = await getDoc(danePodatkoweRef);
          if (danePodatkoweDoc.exists() && danePodatkoweDoc.data().rachunekBankowy) {
            driverRecord.bankAccount = danePodatkoweDoc.data().rachunekBankowy;
          }

          driverRecord.accountStatus = 'Normal';
          resolve(driverRecord);
        } else {
          driverRecord.uzDriverId = 'Duplikat konta użytkownika';
          driverRecord.accountStatus = 'Duplikat';
          resolve(driverRecord);
        }
      });

      driverDataPromises.push(driverDataPromise);
    });

    const resolvedData = await Promise.all(driverDataPromises);
    setDriversData(resolvedData);
    setFilteredDriversData(resolvedData);

    const elapsed = Date.now() - startTime;
    const minimumDisplay = 1000; // минимум 1 секунда
    const delay = Math.max(0, minimumDisplay - elapsed);
    if (!firstLoad) {
      setTimeout(() => {
        setIsProjectChanging(false);
      }, delay);
    } else {
      setFirstLoad(false);
    }
  };

  useEffect(() => {
    updateTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db]);

  useEffect(() => {
    const filtered = driversData.filter((driver) => {
      return (
        driver.driverID.toLowerCase().includes(searchText.toLowerCase()) ||
        driver.balance.toString().includes(searchText) ||
        driver.uzDriverId.toLowerCase().includes(searchText.toLowerCase()) ||
        driver.bankAccount.toLowerCase().includes(searchText.toLowerCase()) ||
        driver.accountStatus.toLowerCase().includes(searchText.toLowerCase())
      );
    });
    setFilteredDriversData(filtered);
  }, [searchText, driversData]);

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleSelectDriver = (driverID) => {
    setSelectedDrivers((prevSelected) => {
      if (prevSelected.includes(driverID)) {
        return prevSelected.filter((id) => id !== driverID);
      } else {
        return [...prevSelected, driverID];
      }
    });
  };

  const openGenerateModal = () => {
    setIsGenerateModalVisible(true);
  };

  const closeGenerateModal = () => {
    setIsGenerateModalVisible(false);
  };

  const openGenerateZUSModal = () => {
    setIsGenerateZUSModalVisible(true);
    setUseAutomaticAmount(true);
    setManualZUSAmount('');
  };

  const closeGenerateZUSModal = () => {
    setIsGenerateZUSModalVisible(false);
    setSelectedMonth(null);
  };

  const openBalancePopup = (driverID, action) => {
    setPopupDriverId(driverID);
    setBalanceAction(action);
    setIsBalancePopupVisible(true);
  };

  const closeBalancePopup = () => {
    setIsBalancePopupVisible(false);
    setBalanceInput('');
  };

  const openBankAccountModal = (driverID, bankAccount) => {
    setPopupDriverId(driverID);
    setBankAccountInput(bankAccount);
    setIsBankAccountModalVisible(true);
  };

  const closeBankAccountModal = () => {
    setIsBankAccountModalVisible(false);
    setBankAccountInput('');
  };

  const handleBankAccountSave = async () => {
    if (bankAccountInput.trim()) {
      try {
        const querySnapshot = await getDocs(
          query(collection(fs, 'users'), where('driverId', '==', popupDriverId))
        );
        let userID = '';

        if (querySnapshot.size === 1) {
          // Пользователь найден, получаем его ID
          const userDoc = querySnapshot.docs[0];
          userID = userDoc.id;
        } else if (querySnapshot.empty) {
          // Пользователь не найден, создаем нового
          const newUserDocRef = await addDoc(collection(fs, 'users'), {
            driverId: popupDriverId,
          });
          userID = newUserDocRef.id;
        } else {
          message.error('Multiple user accounts found.');
          return;
        }

        const danePodatkoweRef = doc(
          fs,
          `users/${userID}/dokumenty`,
          'dane_podatkowe',
        );

        await setDoc(
          danePodatkoweRef,
          { rachunekBankowy: bankAccountInput },
          { merge: true },
        );
        message.success('Bank account updated successfully.');
        closeBankAccountModal();
        updateTable();
      } catch (error) {
        message.error('Failed to update bank account: ' + error.message);
      }
    } else {
      message.error('Please enter a valid bank account.');
    }
  };

  const handleBalanceSave = async () => {
    if (balanceInput && !isNaN(parseFloat(balanceInput))) {
      try {
        const driverRef = ref(db, `drivers/${popupDriverId}`);
        const snapshot = await get(driverRef);
        const currentBalance = parseFloat(snapshot.val().balance || 0);
        const newBalance = Number(
          (
            currentBalance +
            parseFloat(balanceInput) * (balanceAction === 'withdraw' ? -1 : 1)
          ).toFixed(2),
        );
        await update(driverRef, { balance: newBalance });
        message.success(
          `Balance ${balanceAction === 'withdraw' ? 'withdrawn' : 'deposited'} successfully.`,
        );
        setBalanceInput('');
        closeBalancePopup();
        updateTable();
      } catch (error) {
        message.error('Failed to update balance: ' + error.message);
      }
    } else {
      message.error('Please enter a valid amount.');
    }
  };

  const handleGenerateFile = async () => {
    const today = moment();
    const paymentDate = today.format('DD-MM-YYYY');
    let fileContent = '4120414|1\r\n';
    const noBankAccountUsers = [];

    for (const driverID of selectedDrivers) {
      const driver = driversData.find((d) => d.driverID === driverID);
      if (!driver) continue;

      const { bankAccount, balance } = driver;

      if (bankAccount !== 'brak') {
        fileContent += `1|${firmBankAccountConfig}|${bankAccount}|${driverID}|brak|${balance}|1|Rozliczenie|${paymentDate}|\r\n`;

        const driverRef = ref(db, `drivers/${driverID}`);
        await update(driverRef, { balance: 0, balanceUmowaNajmu: 0 }).catch((error) => {
          console.error('Error updating balance:', error);
        });
      } else {
        noBankAccountUsers.push(driverID);
      }
    }

    const blob = new Blob([fileContent], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = 'payments.txt';
    link.click();

    if (noBankAccountUsers.length > 0) {
      alert(
        `Could not process the following users due to missing bank accounts: ${noBankAccountUsers.join(', ')}`,
      );
    }

    message.success('Plik został wygenerowany pomyślnie!');
    closeGenerateModal();
  };

  const handleGenerateZUSFile = async () => {
    if (!selectedMonth) {
      message.error('Please select a month.');
      return;
    }

    let amount;
    if (useAutomaticAmount) {
      amount = 220.29;
    } else {
      if (!manualZUSAmount || isNaN(parseFloat(manualZUSAmount))) {
        message.error('Proszę wpisać poprawną kwotę.');
        return;
      }
      amount = parseFloat(manualZUSAmount);
    }

    const month = moment(selectedMonth).format('MM');
    const year = moment(selectedMonth).format('YYYY');
    const paymentPeriod = `${month}-${year}`;

    const today = moment();
    const paymentDate = today.format('DD-MM-YYYY');

    let fileContent = '4120414|1\r\n';
    const noBankAccountUsers = [];
    const staticAmount = amount.toFixed(2);

    for (const driverID of selectedDrivers) {
      const driver = driversData.find((d) => d.driverID === driverID);
      if (!driver) continue;

      const { bankAccount } = driver;

      if (bankAccount !== 'brak') {
        fileContent += `1|${firmBankAccountConfig}|${bankAccount}|${driverID}|brak|${staticAmount}|1|Wyplata zgodnie z Umowa Zlecenia za okres ${paymentPeriod}|${paymentDate}|\r\n`;

        const driverRef = ref(db, `drivers/${driverID}`);
        const snapshot = await get(driverRef);
        const currentBalance = parseFloat(snapshot.val().balance || 0);
        const currentBalanceUmowaNajmu = parseFloat(snapshot.val().balanceUmowaNajmu || 0);

        const newBalance = (currentBalance - amount).toFixed(2);
        const newBalanceUmowaNajmu = (currentBalanceUmowaNajmu - 281.0).toFixed(2);

        await update(driverRef, {
          balance: newBalance,
          balanceUmowaNajmu: newBalanceUmowaNajmu,
        }).catch((error) => {
          console.error('Error updating balance:', error);
        });
      } else {
        noBankAccountUsers.push(driverID);
      }
    }

    const blob = new Blob([fileContent], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = 'wyplata_zus.txt';
    link.click();

    if (noBankAccountUsers.length > 0) {
      alert(
        `Could not process the following users due to missing bank accounts: ${noBankAccountUsers.join(', ')}`,
      );
    }

    message.success('Plik ZUS został wygenerowany pomyślnie!');
    closeGenerateZUSModal();
  };

  // --- Duplicate handling ---
  const openDuplicateModal = async (driverID) => {
    setDuplicateDriverId(driverID);
    setDuplicateLoading(true);
    setIsDuplicateModalVisible(true);

    try {
      const q = query(collection(fs, 'users'), where('driverId', '==', driverID));
      const querySnapshot = await getDocs(q);
      const accounts = [];

      for (const userDoc of querySnapshot.docs) {
        const userData = userDoc.data();
        const userId = userDoc.id;

        // Load dane_podatkowe
        let danePodatkowe = {};
        try {
          const dpRef = doc(fs, `users/${userId}/dokumenty`, 'dane_podatkowe');
          const dpDoc = await getDoc(dpRef);
          if (dpDoc.exists()) danePodatkowe = dpDoc.data();
        } catch (e) {}

        // Load dane_personalne
        let danePersonalne = {};
        try {
          const dpnRef = doc(fs, `users/${userId}/dokumenty`, 'dane_personalne');
          const dpnDoc = await getDoc(dpnRef);
          if (dpnDoc.exists()) danePersonalne = dpnDoc.data();
        } catch (e) {}

        // Load aplikacje
        let aplikacje = {};
        try {
          const apRef = doc(fs, `users/${userId}/dokumenty`, 'aplikacje');
          const apDoc = await getDoc(apRef);
          if (apDoc.exists()) aplikacje = apDoc.data();
        } catch (e) {}

        // Load samochod from subcollection
        let samochodSub = {};
        try {
          const samRef = doc(fs, `users/${userId}/dokumenty`, 'samochod');
          const samDoc = await getDoc(samRef);
          if (samDoc.exists()) samochodSub = samDoc.data();
        } catch (e) {}

        accounts.push({
          userId,
          ...userData,
          danePodatkowe,
          danePersonalne,
          aplikacje,
          samochodSub,
          createdAt: userData.createdAt || userData.registeredAt || null,
        });
      }

      setDuplicateAccounts(accounts);
    } catch (error) {
      message.error('Błąd ładowania duplikatów: ' + error.message);
    }
    setDuplicateLoading(false);
  };

  const handleDeleteDuplicate = async (userId) => {
    Modal.confirm({
      title: 'Potwierdzenie usunięcia',
      content: `Czy na pewno chcesz usunąć konto ${userId}? Ta operacja jest nieodwracalna.`,
      okText: 'Usuń',
      okType: 'danger',
      cancelText: 'Anuluj',
      onOk: async () => {
        setDeletingAccountId(userId);
        try {
          // Delete subcollections first
          const subCollections = ['dokumenty'];
          for (const subCol of subCollections) {
            const subSnap = await getDocs(collection(fs, `users/${userId}/${subCol}`));
            for (const subDoc of subSnap.docs) {
              await updateDoc(doc(fs, `users/${userId}/${subCol}`, subDoc.id), { _deleted: true });
              // Note: Firestore doesn't support deleting docs from client easily,
              // so we mark as deleted. For full delete use admin SDK.
            }
          }

          // Delete the user document
          await deleteDoc(doc(fs, 'users', userId));

          message.success(`Konto ${userId} zostało usunięte.`);
          setDuplicateAccounts(prev => prev.filter(a => a.userId !== userId));

          // If only one account left, close modal and refresh
          if (duplicateAccounts.length <= 2) {
            setIsDuplicateModalVisible(false);
            updateTable();
          }
        } catch (error) {
          message.error('Błąd usuwania: ' + error.message);
        }
        setDeletingAccountId(null);
      },
    });
  };

  const columns = [
    {
      title: '',
      dataIndex: 'select',
      key: 'select',
      render: (_, record) => (
        <Checkbox
          checked={selectedDrivers.includes(record.driverID)}
          onChange={() => handleSelectDriver(record.driverID)}
          disabled={parseFloat(record.balance) <= 0}
        />
      ),
    },
    {
      title: 'KR Driver ID',
      dataIndex: 'driverID',
      key: 'driverID',
      sorter: (a, b) => a.driverID.localeCompare(b.driverID),
    },
    {
      title: 'Saldo',
      dataIndex: 'balance',
      key: 'balance',
      sorter: (a, b) => parseFloat(a.balance) - parseFloat(b.balance),
    },
    { title: 'UZ Driver ID', dataIndex: 'uzDriverId', key: 'uzDriverId' },
    {
      title: 'Rachunek Bankowy',
      dataIndex: 'bankAccount',
      key: 'bankAccount',
      render: (text, record) => (
        <span
          style={{ color: 'blue', cursor: 'pointer' }}
          onClick={() => openBankAccountModal(record.driverID, record.bankAccount)}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Status konta',
      dataIndex: 'accountStatus',
      key: 'accountStatus',
      render: (text, record) => {
        if (text === 'Duplikat') {
          return (
            <Button
              type="link"
              danger
              style={{ padding: 0, fontWeight: 'bold' }}
              onClick={() => openDuplicateModal(record.driverID)}
            >
              ⚠ Duplikat
            </Button>
          );
        }
        return text;
      },
      filters: [
        { text: 'Normal', value: 'Normal' },
        { text: 'Duplikat', value: 'Duplikat' },
        { text: 'Brak', value: 'Brak' },
      ],
      onFilter: (value, record) => record.accountStatus === value,
    },
    {
      title: 'Wpłata gotówki',
      key: 'deposit',
      render: (_, record) => (
        <Button
          className="sign"
          onClick={() => openBalancePopup(record.driverID, 'deposit')}
          disabled={parseFloat(record.balance) >= 0}
        >
          Wpłata gotówki
        </Button>
      ),
    },
    {
      title: 'Wypłata gotówki',
      key: 'withdraw',
      render: (_, record) => (
        <Button
          className="signNon"
          onClick={() => openBalancePopup(record.driverID, 'withdraw')}
          disabled={parseFloat(record.balance) < 0}
        >
          Wypłata gotówki
        </Button>
      ),
    },
  ];

  const [tableLoading, setTableLoading] = useState(true);

  // Track loading state
  useEffect(() => {
    setTableLoading(driversData.length === 0 && !isProjectChanging);
  }, [driversData, isProjectChanging]);

  return (
    <div>
      <div className="prz-toolbar">
        <Input
          prefix={<MagnifyingGlass size={16} color="#9ca3af" />}
          placeholder="Szukaj kierowcę..."
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
          style={{ flex: 2, minWidth: 150 }}
        />
        <Button
          type="primary"
          disabled={selectedDrivers.length === 0}
          onClick={openGenerateModal}
        >
          <FileText size={15} weight="bold" /> Generuj
        </Button>
        <Button
          disabled={selectedDrivers.length === 0}
          onClick={openGenerateZUSModal}
        >
          <CurrencyDollar size={15} weight="bold" /> Generuj ZUS
        </Button>
      </div>

      <Table
        className="prz-table"
        columns={columns}
        dataSource={filteredDriversData}
        rowKey="driverID"
        loading={driversData.length === 0}
        scroll={{ x: 'max-content' }}
        size="middle"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['15', '25', '50'],
          showTotal: (total, range) => `${range[0]}–${range[1]} z ${total}`,
          size: 'small',
        }}
      />

      <Modal
        title="Generowanie pliku"
        open={isGenerateModalVisible}
        onCancel={closeGenerateModal}
        footer={[
          <Button key="cancel" onClick={closeGenerateModal}>
            Anuluj
          </Button>,
          <Button key="submit" type="primary" onClick={handleGenerateFile}>
            Generuj
          </Button>,
        ]}
      >
        <Table
          dataSource={selectedDrivers.map((driverID) =>
            driversData.find((driver) => driver.driverID === driverID),
          )}
          columns={[
            { title: 'KR Driver ID', dataIndex: 'driverID', key: 'driverID' },
            { title: 'Rachunek Bankowy', dataIndex: 'bankAccount', key: 'bankAccount' },
            { title: 'Saldo', dataIndex: 'balance', key: 'balance' },
          ]}
          rowKey="driverID"
          pagination={false}
        />
      </Modal>

      <Modal
        title="Wybierz miesiąc dla ZUS"
        open={isGenerateZUSModalVisible}
        onCancel={closeGenerateZUSModal}
        footer={[
          <Button key="cancel" onClick={closeGenerateZUSModal}>
            Anuluj
          </Button>,
          <Button key="submit" type="primary" onClick={handleGenerateZUSFile}>
            Generuj ZUS
          </Button>,
        ]}
      >
        <DatePicker
          picker="month"
          format="MM-YYYY"
          value={selectedMonth}
          onChange={(date) => setSelectedMonth(date)}
          style={{ width: '100%', marginBottom: '16px' }}
        />

        <Checkbox
          checked={useAutomaticAmount}
          onChange={(e) => setUseAutomaticAmount(e.target.checked)}
          style={{ marginBottom: '16px' }}
        >
          Użyj automatycznej kwoty 220,29 PLN
        </Checkbox>
        {!useAutomaticAmount && (
          <InputNumber
            min={0}
            placeholder="Wpisz kwotę ZUS"
            value={manualZUSAmount}
            onChange={(value) => setManualZUSAmount(value)}
            style={{ width: '100%', marginBottom: '16px' }}
            stringMode
          />
        )}

        <Table
          dataSource={selectedDrivers.map((driverID) =>
            driversData.find((driver) => driver.driverID === driverID),
          )}
          columns={[
            { title: 'KR Driver ID', dataIndex: 'driverID', key: 'driverID' },
            { title: 'Rachunek Bankowy', dataIndex: 'bankAccount', key: 'bankAccount' },
            { title: 'Saldo', dataIndex: 'balance', key: 'balance' },
          ]}
          rowKey="driverID"
          pagination={false}
        />
      </Modal>

      <Modal
        title={`Zmiana salda dla ${popupDriverId}`}
        open={isBalancePopupVisible}
        onCancel={closeBalancePopup}
        footer={[
          <Button key="cancel" onClick={closeBalancePopup} className="signNon">
            Anuluj
          </Button>,
          <Button key="submit" type="primary" onClick={handleBalanceSave} className="sign">
            Zapisz
          </Button>,
        ]}
      >
        <Input
          placeholder={`Wpisz kwotę do ${
            balanceAction === 'withdraw' ? 'wypłaty' : 'wpłaty'
          }`}
          value={balanceInput}
          onChange={(e) => setBalanceInput(e.target.value)}
        />
      </Modal>

      <Modal
        title={`Zmień rachunek bankowy dla ${popupDriverId}`}
        open={isBankAccountModalVisible}
        onCancel={closeBankAccountModal}
        onOk={handleBankAccountSave}
      >
        <Input
          placeholder="Wpisz nowy numer rachunku bankowego"
          value={bankAccountInput}
          onChange={(e) => setBankAccountInput(e.target.value)}
        />
      </Modal>

      {/* Duplicate accounts modal */}
      <Modal
        title={`Duplikaty kont dla kierowcy: ${duplicateDriverId}`}
        open={isDuplicateModalVisible}
        onCancel={() => setIsDuplicateModalVisible(false)}
        footer={null}
        width={800}
      >
        {duplicateLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
        ) : (
          <div>
            <div style={{ marginBottom: 16, color: '#ff4d4f', fontWeight: 500 }}>
              Znaleziono {duplicateAccounts.length} kont powiązanych z tym kierowcą. Wybierz które konto usunąć.
            </div>
            <Row gutter={16}>
              {duplicateAccounts.map((account, index) => (
                <Col span={12} key={account.userId}>
                  <div style={{
                    border: `2px solid ${index === 0 ? '#52c41a' : '#d9d9d9'}`,
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 16,
                    background: index === 0 ? '#f6ffed' : '#fff',
                  }}>
                    <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ fontSize: 14 }}>Konto {index + 1}</strong>
                      {index === 0 && <span style={{ color: '#52c41a', fontSize: 12, fontWeight: 'bold' }}>✓ Starsze konto</span>}
                    </div>

                    {/* Podstawowe dane */}
                    <div style={{ fontSize: 12, marginBottom: 8, padding: 8, background: '#f0f5ff', borderRadius: 4, borderLeft: '3px solid #1890ff' }}>
                      <strong style={{ color: '#1890ff' }}>Konto</strong>
                      <div><strong>User ID:</strong> <code style={{ fontSize: 10, wordBreak: 'break-all' }}>{account.userId}</code></div>
                      <div><strong>Driver ID:</strong> {account.driverId || '-'}</div>
                      <div><strong>Status:</strong> <span style={{ color: account.status === 'Konto Aktywne' ? '#52c41a' : '#faad14' }}>{account.status || '-'}</span></div>
                      <div><strong>Rola:</strong> {account.role || '-'}</div>
                      <div><strong>Email:</strong> {account.email || '-'}</div>
                      <div><strong>Telefon:</strong> {account.phoneNumber || account.phone || '-'}</div>
                      <div><strong>Obywatelstwo:</strong> {account.citizenship || '-'}</div>
                      {account.firstName && <div><strong>Imię:</strong> {account.firstName}</div>}
                      {account.lastName && <div><strong>Nazwisko:</strong> {account.lastName}</div>}
                      {account.dob && <div><strong>Data urodzenia:</strong> {account.dob}</div>}
                      {account.registeredAt && <div><strong>Rejestracja:</strong> {account.registeredAt?.seconds ? new Date(account.registeredAt.seconds * 1000).toLocaleDateString('pl') : '-'}</div>}
                      {account.okresrozliczeniowy && <div><strong>Okres rozl.:</strong> {account.okresrozliczeniowy}</div>}
                      {account.car_type && <div><strong>Typ auta:</strong> {account.car_type}</div>}
                      {account.rachunekBankowy && <div><strong>Rachunek (root):</strong> {account.rachunekBankowy}</div>}
                      {account.fcmToken && <div><strong>FCM Token:</strong> <span style={{ color: '#52c41a' }}>✓ Tak</span></div>}
                      {account.sumsubApplicantId && <div><strong>Sumsub ID:</strong> <code style={{ fontSize: 10 }}>{account.sumsubApplicantId}</code></div>}
                    </div>

                    {/* Dane personalne */}
                    {account.danePersonalne && Object.keys(account.danePersonalne).length > 0 && (
                      <div style={{ fontSize: 12, marginBottom: 8, padding: 8, background: '#f6ffed', borderRadius: 4, borderLeft: '3px solid #52c41a' }}>
                        <strong style={{ color: '#52c41a' }}>Dane personalne</strong>
                        {account.danePersonalne.pesel && <div><strong>PESEL:</strong> {account.danePersonalne.pesel}</div>}
                        {account.danePersonalne.citizenship && <div><strong>Obywatelstwo:</strong> {account.danePersonalne.citizenship}</div>}
                        {account.danePersonalne.dob && <div><strong>Data urodzenia:</strong> {account.danePersonalne.dob}</div>}
                        {account.danePersonalne.street && <div><strong>Ulica:</strong> {account.danePersonalne.street}</div>}
                        {account.danePersonalne.city && <div><strong>Miasto:</strong> {account.danePersonalne.city}</div>}
                        {account.danePersonalne.postalCode && <div><strong>Kod pocztowy:</strong> {account.danePersonalne.postalCode}</div>}
                        {account.danePersonalne.registrationAddress && <div><strong>Adres zameldowania:</strong> {account.danePersonalne.registrationAddress}</div>}
                        {account.danePersonalne.residenceAddress && <div><strong>Adres zamieszkania:</strong> {account.danePersonalne.residenceAddress}</div>}
                        {account.danePersonalne.correspondenceAddress && <div><strong>Adres korespondencyjny:</strong> {account.danePersonalne.correspondenceAddress}</div>}
                        {account.danePersonalne.status && <div><strong>Status:</strong> {account.danePersonalne.status}</div>}
                      </div>
                    )}

                    {/* Dane podatkowe */}
                    {account.danePodatkowe && Object.keys(account.danePodatkowe).length > 0 && (
                      <div style={{ fontSize: 12, marginBottom: 8, padding: 8, background: '#fff7e6', borderRadius: 4, borderLeft: '3px solid #faad14' }}>
                        <strong style={{ color: '#faad14' }}>Dane podatkowe</strong>
                        {account.danePodatkowe.rachunekBankowy && <div><strong>Rachunek bankowy:</strong> {account.danePodatkowe.rachunekBankowy}</div>}
                        {account.danePodatkowe.nip && <div><strong>NIP:</strong> {account.danePodatkowe.nip}</div>}
                        {account.danePodatkowe.urzadSkarbowy && <div><strong>Urząd skarbowy:</strong> {account.danePodatkowe.urzadSkarbowy}</div>}
                        {account.danePodatkowe.okresrozliczeniowy && <div><strong>Okres rozl.:</strong> {account.danePodatkowe.okresrozliczeniowy}</div>}
                        {account.danePodatkowe.settlementPeriod && <div><strong>Settlement:</strong> {account.danePodatkowe.settlementPeriod}</div>}
                      </div>
                    )}

                    {/* Aplikacje */}
                    {account.aplikacje && Object.keys(account.aplikacje).length > 0 && (
                      <div style={{ fontSize: 12, marginBottom: 8, padding: 8, background: '#f9f0ff', borderRadius: 4, borderLeft: '3px solid #722ed1' }}>
                        <strong style={{ color: '#722ed1' }}>Aplikacje</strong>
                        <div>{Object.entries(account.aplikacje).map(([app, active]) => (
                          <span key={app} style={{ marginRight: 8 }}>
                            {active ? '✅' : '❌'} {app}
                          </span>
                        ))}</div>
                      </div>
                    )}

                    {/* Samochód */}
                    {account.samochodSub && Object.keys(account.samochodSub).length > 0 && (
                      <div style={{ fontSize: 12, marginBottom: 8, padding: 8, background: '#e6f7ff', borderRadius: 4, borderLeft: '3px solid #13c2c2' }}>
                        <strong style={{ color: '#13c2c2' }}>Samochód</strong>
                        {account.samochodSub.carOwnership && <div><strong>Własność:</strong> {account.samochodSub.carOwnership}</div>}
                        {account.samochodSub.citizenship && <div><strong>Citizenship:</strong> {account.samochodSub.citizenship}</div>}
                      </div>
                    )}

                    {/* Dokumenty z root (jeśli są) */}
                    {account.dokumenty && (
                      <div style={{ fontSize: 12, marginBottom: 8, padding: 8, background: '#fafafa', borderRadius: 4, borderLeft: '3px solid #999' }}>
                        <strong>Dokumenty (root)</strong>
                        {Object.entries(account.dokumenty).map(([docType, docData]) => (
                          <div key={docType}>
                            <strong>{docType}:</strong> {typeof docData === 'object' ? (docData?.status || JSON.stringify(docData).substring(0, 50)) : String(docData)}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Samochód z root (jeśli jest) */}
                    {account.samochod && typeof account.samochod === 'object' && (
                      <div style={{ fontSize: 12, marginBottom: 8, padding: 8, background: '#fafafa', borderRadius: 4, borderLeft: '3px solid #999' }}>
                        <strong>Samochód (root)</strong>
                        {Object.entries(account.samochod).map(([key, val]) => (
                          <div key={key}>
                            <strong>{key}:</strong> {typeof val === 'object' ? (val?.status || 'OK') : String(val)}
                          </div>
                        ))}
                      </div>
                    )}

                    <Button
                      danger
                      block
                      loading={deletingAccountId === account.userId}
                      onClick={() => handleDeleteDuplicate(account.userId)}
                      style={{ marginTop: 12 }}
                    >
                      🗑 Usuń to konto
                    </Button>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Modal>

      {/* Оверлей для плавного отображения при переключении проекта */}
      {isProjectChanging && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255,255,255,0.8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            transition: 'opacity 0.5s ease',
          }}
        >
          <Spin size="large" />
          <div style={{ marginTop: 16, fontSize: 18, color: '#333' }}>Przełączam projekt...</div>
        </div>
      )}
    </div>
  );
};

export default Przelewy;
